import { useWebsocket } from 'contexts/websocket';
import { useEffect, useRef } from 'react';
import { getChatMessagesVersion } from 'utils/chat/chatState';

/**
 * Websocket drift detector for chat summaries.
 *
 * Use when a component already has chat summaries and needs to know if any are stale.
 * The hook contributes message versions to ping payloads and calls `onOutOfSync(chatId)`
 * for each server-reported mismatch; callers decide how to refresh that chat.
 *
 * @param {Object} props
 * @param {Array<Object>} props.chats Chat summaries to include in reconciliation pings.
 * @param {boolean} [props.enabled=true] Whether ping payloads and pong handling are active.
 * @param {Function} props.onOutOfSync Called with the stale chat id.
 * @returns {void}
 */
const useChatSummariesReconciliation = ({ chats, enabled = true, onOutOfSync }) => {
    const { onMessage, setPingPayload } = useWebsocket();
    const syncingChatIdsRef = useRef(new Set());

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!enabled) return {};

            const chatReconciliations = (chats || [])
                .filter((chat) => chat?.id)
                .map((chat) => ({
                    chat_id: chat.id,
                    messages_version: getChatMessagesVersion(chat),
                }));

            if (chatReconciliations.length === 0) return {};

            return {
                chat_reconciliations: chatReconciliations,
            };
        });

        return cleanup;
    }, [chats, enabled, setPingPayload]);

    useEffect(() => {
        if (!enabled || !onOutOfSync) return;

        const cleanup = onMessage((message) => {
            if (message.type !== 'pong' || !Array.isArray(message.chat_reconciliations)) return;

            message.chat_reconciliations.forEach(({ chat_id: chatId, out_of_sync: outOfSync }) => {
                if (!chatId || !outOfSync || syncingChatIdsRef.current.has(chatId)) return;

                syncingChatIdsRef.current.add(chatId);

                Promise.resolve(onOutOfSync(chatId)).finally(() => {
                    syncingChatIdsRef.current.delete(chatId);
                });
            });
        });

        return cleanup;
    }, [enabled, onMessage, onOutOfSync]);
};

export default useChatSummariesReconciliation;
